import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Product } from '../../models/entities/product';
import { SubscriptionTemplate } from '../../models/entities/subscription-template';

@Injectable({
    providedIn: 'root',
})
export class PlanService
{
    public static PricingBases: string[] = [
        'month',
        'year',
        'unit-month',
    ];

    private _http: ApplicationHttpClient;

    constructor(_http: ApplicationHttpClient) {
        this._http = _http;
    }

    /**
     * @returns {Observable<Object>}
     */
    public getProducts(): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Products);

        return this._http.get(url);
    }

    /**
     * @param {Product} product
     * @returns {Observable<Object>}
     */
    public addProduct(product: Product): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Products);

        return this._http.post(url, JSON.stringify(product));
    }

    /**
     * @param {number} productId
     * @param {Product} product
     * @returns {Observable<Object>}
     */
    public editProduct(productId: number, product: Product): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Product(productId));

        return this._http.put(url, JSON.stringify(product));
    }

    /**
     * @returns {Observable<Object>}
     */
    public getSubscriptionTemplates(): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.SubscriptionTemplates);

        return this._http.get(url);
    }

    /**
     * @param {number} organizationId
     * @param {number} subscriptionId
     * @returns {Observable<Object>}
     */
    public getSubscription(organizationId: number, subscriptionId: number): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Admin +
                Routes.LegFiCore.OrgSubscription(organizationId, subscriptionId),
        );

        return this._http.get(url);
    }

    /**
     * @param {SubscriptionTemplate} subscriptionTemplate
     * @returns {Observable<Object>}
     */
    public addSubscriptionTemplate(subscriptionTemplate: SubscriptionTemplate): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Admin + Routes.LegFiCore.SubscriptionTemplates);

        return this._http.post(url, JSON.stringify(subscriptionTemplate));
    }

    /**
     * @param {number} subscriptionTemplateId
     * @param {SubscriptionTemplate} subscriptionTemplate
     * @returns {Observable<Object>}
     */
    public editSubscriptionTemplate(
            subscriptionTemplateId: number,
            subscriptionTemplate: SubscriptionTemplate,
    ): Observable<Object> {
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Admin + Routes.LegFiCore.SubscriptionTemplate(subscriptionTemplateId));

        return this._http.put(url, JSON.stringify(subscriptionTemplate));
    }

    /**
     * @param {number} organizationId
     * @param {any} subscription
     * @returns {Observable<Object>}
     */
    public createSubscription(organizationId: number, subscription: any): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Admin + Routes.LegFiCore.OrgSubscriptions(organizationId || jwt.orgId),
        );

        return this._http.post(url, JSON.stringify(subscription));
    }

    /**
     * @param {number} organizationId
     * @param {any} subscription
     * @returns {Observable<Object>}
     */
    public saveSubscription(organizationId: number, subscription: any): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Admin + Routes.LegFiCore.OrgSubscription(
                        organizationId || jwt.orgId, subscription.id,
                ),
        );

        return this._http.put(url, JSON.stringify(subscription));
    }

    /**
     * @param organizationId
     * @param subscriptionId
     * @returns {any}
     */
    public deleteSubscription(organizationId: number, subscriptionId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.Admin + Routes.LegFiCore.OrgSubscription(
                        organizationId, subscriptionId,
                ),
        );

        return this._http.delete(url);
    }
}
